/**
 * API Keys 管理页面
 * 用户可以管理自己的 API 令牌
 */

import { LayoutWithSidebar } from "@/components/LayoutWithSidebar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	type Token,
	TokenStatus,
	copyTokenKey,
	createToken,
	deleteToken,
	formatTimestamp,
	formatTokenStatus,
	getTokenStatusColor,
	getTokens,
	maskTokenKey,
	updateTokenStatus,
} from "@/services/tokenApi";
import { AlertTriangle, Shield } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

export function APIKeysPage() {
	// 令牌管理状态
	const [tokens, setTokens] = useState<Token[]>([]);
	const [loadingTokens, setLoadingTokens] = useState(false);
	const [showCreateTokenDialog, setShowCreateTokenDialog] = useState(false);
	const [newTokenName, setNewTokenName] = useState("");
	const [creatingToken, setCreatingToken] = useState(false);

	// 加载令牌列表
	const loadTokens = useCallback(async () => {
		try {
			setLoadingTokens(true);
			const response = await getTokens();
			if (response.success) {
				setTokens(response.data);
			}
		} catch (error: any) {
			console.error("获取令牌列表失败:", error);
			toast.error(error.message || "获取令牌列表失败");
		} finally {
			setLoadingTokens(false);
		}
	}, []);

	// 创建新令牌
	const handleCreateToken = async () => {
		if (!newTokenName.trim()) {
			toast.error("请输入 API Key 名称");
			return;
		}

		if (newTokenName.length > 30) {
			toast.error("API Key 名称不能超过30个字符");
			return;
		}

		setCreatingToken(true);
		try {
			const response = await createToken(newTokenName.trim());
			if (response.success) {
				toast.success("API Key 创建成功");
				setNewTokenName("");
				setShowCreateTokenDialog(false);
				await loadTokens();
			}
		} catch (error: any) {
			console.error("创建 API Key 失败:", error);
			toast.error(error.message || "创建 API Key 失败");
		} finally {
			setCreatingToken(false);
		}
	};

	// 删除令牌
	const handleDeleteToken = async (tokenId: number, tokenName: string) => {
		if (!confirm(`确定要删除 API Key "${tokenName}"吗？此操作不可恢复。`)) {
			return;
		}

		try {
			const response = await deleteToken(tokenId);
			if (response.success) {
				toast.success("API Key 删除成功");
				await loadTokens();
			}
		} catch (error: any) {
			console.error("删除 API Key 失败:", error);
			toast.error(error.message || "删除 API Key 失败");
		}
	};

	// 更新令牌状态
	const handleUpdateTokenStatus = async (
		tokenId: number,
		status: TokenStatus,
	) => {
		try {
			const response = await updateTokenStatus(tokenId, status);
			if (response.success) {
				toast.success("API Key 状态更新成功");
				await loadTokens();
			}
		} catch (error: any) {
			console.error("更新 API Key 状态失败:", error);
			toast.error(error.message || "更新 API Key 状态失败");
		}
	};

	// 复制令牌密钥
	const handleCopyTokenKey = async (key: string) => {
		const success = await copyTokenKey(key);
		if (success) {
			toast.success("API Key 已复制到剪贴板");
		} else {
			toast.error("复制失败，请手动复制");
		}
	};

	useEffect(() => {
		loadTokens();
	}, [loadTokens]);

	return (
		<LayoutWithSidebar title="API Keys">
			<div className="flex flex-1 flex-col p-4">
				<div className="container mx-auto max-w-6xl">
						<div className="flex flex-col gap-6 py-6">
							{/* 页面标题 */}
							<div>
								<h1 className="text-3xl font-bold text-gray-900">API Keys</h1>
								<p className="text-gray-500 mt-2">管理您的 API 访问密钥</p>
							</div>

							{/* 安全提示 */}
							<Card className="bg-yellow-50 border-yellow-200">
								<CardContent className="pt-6">
									<div className="flex gap-3">
										<AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
										<div className="flex-1">
											<h3 className="font-medium text-yellow-900 mb-2">
												安全提示
											</h3>
											<p className="text-sm text-yellow-800 leading-relaxed">
												列表内是您的全部 API Keys，请不要与他人共享您的 API
												Keys，避免将其暴露在浏览器和其他客户端代码中。为了保护您帐户的安全，我们还可能会自动更换我们发现已公开泄露的密钥信息。
											</p>
										</div>
									</div>
								</CardContent>
							</Card>

							{/* API Keys 列表 */}
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center justify-between">
										<div className="flex items-center gap-2">
											<Shield className="h-5 w-5" />
											我的 API Keys
										</div>
										<Button
											onClick={() => setShowCreateTokenDialog(true)}
											size="sm"
											className="bg-blue-600 hover:bg-blue-700"
										>
											+ 新建 API Key
										</Button>
									</CardTitle>
									<CardDescription>
										所有 API Keys 永不过期且无限额度
									</CardDescription>
								</CardHeader>
								<CardContent>
									{loadingTokens ? (
										<div className="text-center py-8">
											<p className="text-gray-500">加载中...</p>
										</div>
									) : tokens.length === 0 ? (
										<div className="text-center py-8">
											<Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
											<p className="text-gray-500 mb-2">暂无 API Keys</p>
											<p className="text-sm text-gray-400 mb-4">
												创建 API Key 后可用于 API 调用
											</p>
											<Button
												onClick={() => setShowCreateTokenDialog(true)}
												variant="outline"
											>
												+ 新建 API Key
											</Button>
										</div>
									) : (
										<div className="space-y-3">
											{tokens.map((token) => (
												<div
													key={token.id}
													className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
												>
													<div className="flex items-start justify-between mb-3">
														<div className="flex-1">
															<div className="flex items-center gap-2 mb-1">
																<h4 className="font-medium text-gray-900">
																	{token.name}
																</h4>
																<Badge
																	className={getTokenStatusColor(token.status)}
																>
																	{formatTokenStatus(token.status)}
																</Badge>
															</div>
															<div className="flex items-center gap-2 text-xs text-gray-600">
																<span>ID: {token.id}</span>
																<span>•</span>
																<span>
																	创建于 {formatTimestamp(token.created_time)}
																</span>
																{token.accessed_time && (
																	<>
																		<span>•</span>
																		<span>
																			最后访问{" "}
																			{formatTimestamp(token.accessed_time)}
																		</span>
																	</>
																)}
															</div>
														</div>
														<div className="flex items-center gap-2">
															<Button
																variant="outline"
																size="sm"
																onClick={() => handleCopyTokenKey(token.key)}
																title="复制 API Key"
															>
																复制
															</Button>
															{token.status === TokenStatus.ENABLED ? (
																<Button
																	variant="outline"
																	size="sm"
																	onClick={() =>
																		handleUpdateTokenStatus(
																			token.id,
																			TokenStatus.DISABLED,
																		)
																	}
																	title="禁用 API Key"
																>
																	禁用
																</Button>
															) : (
																<Button
																	variant="outline"
																	size="sm"
																	onClick={() =>
																		handleUpdateTokenStatus(
																			token.id,
																			TokenStatus.ENABLED,
																		)
																	}
																	title="启用 API Key"
																>
																	启用
																</Button>
															)}
															<Button
																variant="outline"
																size="sm"
																onClick={() =>
																	handleDeleteToken(token.id, token.name)
																}
																className="text-red-600 hover:text-red-700 hover:bg-red-50"
																title="删除 API Key"
															>
																删除
															</Button>
														</div>
													</div>
													<div className="space-y-2">
														<div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
															<code className="flex-1 font-mono text-xs text-gray-700 break-all">
																{maskTokenKey(token.key)}
															</code>
														</div>
														<div className="flex items-center justify-between text-xs text-gray-600">
															<span>永不过期</span>
															<span>无限额度</span>
														</div>
													</div>
												</div>
											))}
										</div>
									)}
								</CardContent>
							</Card>
						</div>
					</div>
				</div>

			{/* 创建 API Key 对话框 */}
			{showCreateTokenDialog && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
							<h3 className="text-lg font-semibold mb-4">新建 API Key</h3>
							<div className="space-y-4">
								<div>
									<Label htmlFor="tokenName">描述</Label>
									<Input
										id="tokenName"
										value={newTokenName}
										onChange={(e) => setNewTokenName(e.target.value)}
										placeholder="API key名称，用来区分API key"
										maxLength={30}
										autoFocus
									/>
									<p className="text-xs text-gray-500 mt-1">最多 30 个字符</p>
								</div>
								<div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
									<p className="font-medium mb-1">API Key 配置：</p>
									<ul className="text-xs space-y-1 text-blue-700">
										<li>• 永不过期</li>
										<li>• 无限额度</li>
										<li>• 适用于所有 API 调用</li>
									</ul>
								</div>
							</div>
							<div className="flex justify-end gap-3 mt-6">
								<Button
									variant="outline"
									onClick={() => {
										setShowCreateTokenDialog(false);
										setNewTokenName("");
									}}
									disabled={creatingToken}
								>
									取消
								</Button>
								<Button
									onClick={handleCreateToken}
									disabled={creatingToken || !newTokenName.trim()}
									className="bg-blue-600 hover:bg-blue-700"
								>
									{creatingToken ? "创建中..." : "创建"}
								</Button>
							</div>
				</div>
			</div>
			)}
		</LayoutWithSidebar>
	);
}

export default APIKeysPage;
